import clsx from 'clsx';
import type React from 'react';

/**
 * Navigation bar in the header.
 *
 * <HeaderBar>
 *   <HeaderBarMain>
 *     <HeaderBarBreadcrumbs>
 *       <Breadcrumbs />
 *     </HeaderBarBreadcrumbs>
 *     <HeaderBarTabs>
 *       <ButtonTabs>
 *         <ButtonTab label="View" />
 *         <ButtonTab label="Edit" />
 *       </ButtonTabs>
 *     </HeaderBarTabs>
 *   </HeaderBarMain>
 *   <HeaderBarActions>
 *     <HeaderBarActionsSide>
 *       <Button label="Integrations" kind="blank" iconOnly />
 *       <Button label="Share" kind="blank" iconOnly />
 *     </HeaderBarActionsSide>
 *     <HeaderBarActionsMain>
 *      <Button label="Visit" kind="primary" />
 *     </HeaderBarActionsMain>
 *   </HeaderBarActions>
 * </HeaderBar>
 */
type HeaderBarBreakpoint = '2xl' | '4xl' | '5xl';

export function HeaderBar(
    props: React.PropsWithChildren<
        React.HTMLAttributes<HTMLElement> & { breakpoint?: HeaderBarBreakpoint }
    >
) {
    const { breakpoint = '2xl', children, ...rest } = props;
    return (
        <header className="@container/header" {...rest}>
            <div
                className={clsx('flex flex-col gap-x-3 border-b', {
                    '@5xl/header:flex-row @5xl/header:items-center @5xl/header:px-2.5':
                        breakpoint === '5xl',
                    '@4xl/header:flex-row @4xl/header:items-center @4xl/header:px-2.5':
                        breakpoint === '4xl',
                    '@2xl/header:flex-row @2xl/header:items-center @2xl/header:px-2.5':
                        breakpoint === '2xl',
                })}
            >
                {children}
            </div>
        </header>
    );
}

/**
 * Main section of the header bar, containing breadcrumbs and tabs.
 */
export function HeaderBarMain(
    props: React.PropsWithChildren & { breakpoint?: HeaderBarBreakpoint }
) {
    const { breakpoint = '2xl', children } = props;
    return (
        <div
            className={clsx(
                'flex h-[calc(var(--app-toolbar-height)-1px)] items-center gap-2 px-5',
                {
                    '@5xl/header:flex-1 @5xl/header:p-0': breakpoint === '5xl',
                    '@4xl/header:flex-1 @4xl/header:p-0': breakpoint === '4xl',
                    '@2xl/header:flex-1 @2xl/header:p-0': breakpoint === '2xl',
                }
            )}
        >
            {children}
        </div>
    );
}

/**
 * Breadcrumbs in the header bar.
 */
export function HeaderBarBreadcrumbs(props: React.PropsWithChildren) {
    return <div className="flex items-center gap-1">{props.children}</div>;
}

/**
 * Tabs in the header bar.
 */
export function HeaderBarTabs(
    props: React.PropsWithChildren & { breakpoint?: HeaderBarBreakpoint }
) {
    const { breakpoint = '2xl', children } = props;
    return (
        <div
            className={clsx('flex flex-1 gap-1 overflow-auto', {
                '@5xl/header:min-w-min @5xl/header:shrink-0 @5xl/header:overflow-visible':
                    breakpoint === '5xl',
                '@4xl/header:min-w-min @4xl/header:shrink-0 @4xl/header:overflow-visible':
                    breakpoint === '4xl',
                '@2xl/header:min-w-min @2xl/header:shrink-0 @2xl/header:overflow-visible':
                    breakpoint === '2xl',
            })}
        >
            {children}
        </div>
    );
}

/**
 * Actions in the header bar.
 */
export function HeaderBarActions(
    props: React.PropsWithChildren & { breakpoint?: HeaderBarBreakpoint }
) {
    const { breakpoint = '2xl', children } = props;
    return (
        <div
            className={clsx(
                'flex h-[calc(var(--app-toolbar-height)-1px)] items-center border-t px-5',
                {
                    '@5xl/header:min-w-min @5xl/header:shrink-0 @5xl/header:border-t-0 @5xl/header:px-0':
                        breakpoint === '5xl',
                    '@4xl/header:min-w-min @4xl/header:shrink-0 @4xl/header:border-t-0 @4xl/header:px-0':
                        breakpoint === '4xl',
                    '@2xl/header:min-w-min @2xl/header:shrink-0 @2xl/header:border-t-0 @2xl/header:px-0':
                        breakpoint === '2xl',
                }
            )}
        >
            {children}
        </div>
    );
}

/**
 * Side actions in the header bar.
 * Intended for secondary actions like "Integrations" or "Member access".
 */
export function HeaderBarActionsSide(
    props: React.PropsWithChildren & { breakpoint?: HeaderBarBreakpoint }
) {
    const { breakpoint = '2xl', children } = props;
    return (
        <div
            className={clsx('flex items-center gap-1 overflow-auto empty:hidden', {
                '@5xl/header:overflow-visible': breakpoint === '5xl',
                '@4xl/header:overflow-visible': breakpoint === '4xl',
                '@2xl/header:overflow-visible': breakpoint === '2xl',
            })}
        >
            {children}
        </div>
    );
}

/**
 * Separator used to split actions into smaller groups.
 */
export function HeaderActionsSeparator() {
    return <div className="hidden h-6 border-r sm:flex" />;
}

/**
 * Main actions in the header bar.
 * Intended for primary actions like "Visit" or "Edit".
 */
export function HeaderBarActionsMain(
    props: React.PropsWithChildren & { breakpoint?: HeaderBarBreakpoint }
) {
    const { breakpoint = '2xl', children } = props;
    return (
        <div
            className={clsx(
                'flex shrink-0 flex-grow items-center justify-end gap-1.5 px-2 first:pl-0 last:pr-0',
                {
                    '@5xl/header:flex-grow-0': breakpoint === '5xl',
                    '@4xl/header:flex-grow-0': breakpoint === '4xl',
                    '@2xl/header:flex-grow-0': breakpoint === '2xl',
                }
            )}
        >
            {children}
        </div>
    );
}
